import React, { useEffect, useState } from 'react';
import { EmptyState } from '../../../components/EmptyState';
import { BubbleTable } from '../../../components/BubbleTable';
import { InterviewListItem } from '../../../entities/InterviewListItem';
import { Application } from '../../../entities/Application';
import { InterviewRow } from './InterviewRow';
import { CancelInterviewConfirmationModal } from './CancelInterviewConfirmationModal';
import { User } from '../../../entities/User';
import { JobHiringMemberRole } from '../../../entities/JobHiringMemberRole';
import { AlertObject, AlertType } from '../../../components/Alert';
import { CandidatePageSubHeader } from '../CandidatePageSubHeader';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { ApplicationService as ATApplicationService } from '../../../services/applicant_tracking/ApplicationService';
import { ApplicationService as PlatformApplicationService } from '../../../services/ApplicationService';

interface PropTypes {
  currentUser: User;
  application: Application;
  applicationId: number;
  applicantTrackingEnabled: boolean;
  jobHiringMemberRole: JobHiringMemberRole;
  onInterviewSelected: (interview: InterviewListItem) => void;
  setAlert: (alert: AlertObject) => void;
}

interface TablePropTypes {
  applicationId: number;
  applicantTrackingEnabled: boolean;
  currentUser: User;
  jobHiringMemberRole: JobHiringMemberRole;
  interviews: InterviewListItem[];
  isLoading: boolean;
  onCancelInterview: (interviewId: string) => void;
  onInterviewSelected: (interview: InterviewListItem) => void;
}

function InterviewsTable(props: TablePropTypes) {
  if (props.isLoading) {
    return <LoadingSpinner />;
  }

  if (props.interviews.length === 0) {
    return (
      <EmptyState
        classNames={'mt-3'}
        title='No interviews are available for this candidate yet'
      />
    );
  }

  return (
    <BubbleTable>
      <thead>
        <tr>
          <th className='left-col'>Interview</th>
          <th>Status</th>
          <th>Participants</th>
          <th>Duration</th>
          <th>Scheduled For</th>
          <th>Interview Rating</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        {props.interviews.map((interview: InterviewListItem, idx: number) => (
          <InterviewRow
            key={idx}
            applicationId={props.applicationId}
            currentUser={props.currentUser}
            interview={interview}
            jobHiringMemberRole={props.jobHiringMemberRole}
            onInterviewSelected={props.onInterviewSelected}
            onCancelInterview={props.onCancelInterview}
            applicantTrackingEnabled={props.applicantTrackingEnabled}
          />
        ))}
      </tbody>
    </BubbleTable>
  );
}

export function InterviewsTab(props: PropTypes) {
  const [interviewToBeCancelled, setInterviewToBeCancelled] = useState(null);
  const [isCancelInterviewModalOpen, setIsCancelInterviewModalOpen] =
    useState(false);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [interviews, setInterviews] = useState<InterviewListItem[]>([]);

  useEffect(() => {
    loadInterviews();
  }, []);

  const loadInterviews = () => {
    setIsLoading(true);

    if (props.applicantTrackingEnabled) {
      ATApplicationService.listInterviews(props.applicationId)
        .then((interviewListItems) => setInterviews(interviewListItems))
        .catch((e) => {
          console.error(e);
        })
        .finally(() => setIsLoading(false));
    } else {
      PlatformApplicationService.listInterviews(props.applicationId)
        .then((interviewListItems) => setInterviews(interviewListItems))
        .catch((e) => {
          console.error(e);
        })
        .finally(() => setIsLoading(false));
    }
  };

  const handleCancelInterview = (interviewId: string) => {
    setInterviewToBeCancelled(interviewId);
    setIsCancelInterviewModalOpen(true);
  };

  const handleCancellingSuccess = () => {
    loadInterviews();
    props.setAlert({
      message: `The interview with ${props.application.candidate.name} was successfully cancelled.`,
      type: AlertType.Success,
    });
  };

  const handleCancellingError = () => {
    props.setAlert({
      message: `The interview with ${props.application.candidate.name} could not be cancelled.`,
      type: AlertType.Danger,
    });
  };

  return (
    <>
      <CandidatePageSubHeader text='Interviews' />
      <CancelInterviewConfirmationModal
        candidateName={props.application.candidate.name}
        interviewId={interviewToBeCancelled}
        isModalOpen={isCancelInterviewModalOpen}
        setIsModalOpen={setIsCancelInterviewModalOpen}
        onSuccess={handleCancellingSuccess}
        onError={handleCancellingError}
      />
      <InterviewsTable
        applicationId={props.applicationId}
        applicantTrackingEnabled={props.applicantTrackingEnabled}
        currentUser={props.currentUser}
        jobHiringMemberRole={props.jobHiringMemberRole}
        interviews={interviews}
        isLoading={isLoading}
        onCancelInterview={handleCancelInterview}
        onInterviewSelected={props.onInterviewSelected}
      />
    </>
  );
}
